import { PaperClipOutlined } from "@ant-design/icons";
import { Empty, Typography } from "antd";
import InlineAttachmentViewer from "components/molecules/InlineAttachmentViewer";
import AttachmentLightbox from "components/organisms/AttachmentLightbox";
import { useMemo, useState } from "react";

const { Title, Text } = Typography;

const AttachmentTab = ({ dataObject, schemaFields = [], onDownload, piiProtected = false, className = "" }) => {
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const attachmentFields = useMemo(() => {
        return schemaFields.filter((field) => ["attachment", "image", "document"].includes(field.dataType));
    }, [schemaFields]);

    const allAttachments = useMemo(() => {
        const attachments = [];

        attachmentFields.forEach((field) => {
            const fieldValue = dataObject?.[field.fieldId];
            if (fieldValue) {
                try {
                    const parsedValue = JSON.parse(fieldValue);
                    const files = Array.isArray(parsedValue) ? parsedValue : [parsedValue];

                    files.forEach((file) => {
                        if (file && file.key) {
                            attachments.push({
                                ...file,
                                fieldId: field.fieldId,
                                fieldName: field.name,
                                fieldDescription: field.description,
                            });
                        }
                    });
                } catch (error) {
                    console.error(`Error parsing attachment field ${field.fieldId}:`, error);
                }
            }
        });

        return attachments;
    }, [dataObject, attachmentFields]);

    const handlePreview = (file) => {
        const index = allAttachments.findIndex((att) => att.key === file.key);
        if (index !== -1) {
            setLightboxIndex(index);
            setLightboxVisible(true);
        }
    };

    const handleCloseLightbox = () => {
        setLightboxVisible(false);
    };

    const handleDownload = (file) => {
        if (onDownload) {
            onDownload(file);
        }
    };

    if (allAttachments.length === 0) {
        return (
            <div className={`attachment-tab p-4 ${className}`}>
                <div className="flex h-64 items-center justify-center">
                    <Empty image={<PaperClipOutlined className="text-4xl text-gray-300" />} description="No attachments found" className="text-gray-500" />
                </div>
            </div>
        );
    }

    return (
        <div className={`attachment-tab p-4 ${className}`}>
            <div className="mb-6">
                <Title level={4} className="mb-2 flex items-center">
                    <PaperClipOutlined className="mr-2" />
                    Attachments ({allAttachments.length})
                </Title>
                <Text className="text-gray-600">View and download attachments for this record</Text>
            </div>

            {attachmentFields.map((field) => {
                const fieldValue = dataObject?.[field.fieldId];
                if (!fieldValue) return null;

                let fieldAttachments = [];
                try {
                    const parsedValue = JSON.parse(fieldValue);
                    fieldAttachments = Array.isArray(parsedValue) ? parsedValue : [parsedValue];
                } catch (error) {
                    return null;
                }

                if (fieldAttachments.length === 0) return null;

                return (
                    <div key={field.fieldId} className="mb-6">
                        <div className="mb-4">
                            <Title level={5} className="mb-1">
                                {field.name}
                            </Title>
                            {field.description && <Text className="text-sm text-gray-500">{field.description}</Text>}
                        </div>

                        <InlineAttachmentViewer
                            files={fieldAttachments}
                            onPreview={handlePreview}
                            onDownload={handleDownload}
                            layout="carousel"
                            showControls={true}
                            maxThumbnails={12}
                        />
                    </div>
                );
            })}

            <AttachmentLightbox visible={lightboxVisible} files={allAttachments} currentIndex={lightboxIndex} onClose={handleCloseLightbox} onDownload={handleDownload} piiProtected={piiProtected} />
        </div>
    );
};

export default AttachmentTab;
